import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References, FileMeta } from '/imports/types';

/**
 * Cards are keyed by MTGO-assigned numeric IDs. We expose them through the
 * same CRUD surface as other collections; method handlers coerce the `_id`
 * string from the UI to a number at the Mongo query boundary.
 */
export interface Card extends Doc {
  _id: number;                 // MTGO catalog id from the client XML (methods coerce the UI's string form)
  name: string;                // card name (CARDNAME_STRING table, via importCardXml)
  setName: string;             // set CODE despite the name, e.g. '7E', 'FIN' (CARDSETNAME_STRING)
  rarity: string;              // MTGO rarity: COMMON|UNCOMMON|RARE|MYTHIC|BASIC_LAND|TOKEN|PROMO|PLAY_AID|BONUS|''
  isfoil: boolean;             // foil printing (XML IS_FOIL)
  legacy: boolean;             // Legacy-format flag used by store/card-list queries; data-maintained, not set by the XML import
  oldset: boolean;             // printing belongs to an old/retired set (XML OLDSET === "1")
  promo: boolean;              // promotional printing (setName === 'PRM')
  booster: boolean;            // doc is a booster-pack PRODUCT, not a card (XML IS_BOOSTER); gets store product images
  legendary: boolean;          // legendary permanent (XML IS_LEGENDARY); feeds the composed typeLine
  redeemable: boolean;         // set can be redeemed for paper (setName in REDEEMABLESETS)
  pauper: boolean;             // Pauper-format flag used by store/card-list queries; data-maintained, not set by the XML import
  sticky: boolean;             // vestigial Python-era "preserve across card-DB rebuild" flag; the TS upsert import made it moot
  gbBuyPrice: number | null;   // goatbots' buy price from the scraper (market reference; null = goatbots doesn't price it)
  gbSellPrice: number | null;  // goatbots' sell price from the scraper
  manBuyPrice: number | null;  // price our bots PAY traders; derived from gbBuyPrice by margin/strategy unless fixedPrice
  manSellPrice: number | null; // price our bots CHARGE traders; derived from gbSellPrice unless fixedPrice
  cashBuyPrice: number | null; // USD price cash bots pay; derived as CASHBUYFACTOR * gbBuyPrice unless fixedPrice
  buyPriceMax: number | null;  // Python-era cap for the global price adjuster on manBuyPrice; carried over, unread in TS
  sellPriceMin: number | null; // Python-era floor for the global price adjuster on manSellPrice; carried over, unread in TS
  gbBuyMargin?: number | null;  // per-card % margin: scraper derives manBuyPrice = gbBuyPrice * (1 + m/100), overriding set-level margin
  gbSellMargin?: number | null; // per-card % margin for manSellPrice, same derivation as gbBuyMargin
  fixedPrice?: boolean;        // operator owns man/cash prices: scraper still records gbBuy/gbSellPrice but never derives from them
  images?: FileMeta[];         // card face image(s) scraped from gatherer.wizards.com; multiple = DFC faces / alt arts
  thumbs?: FileMeta[];         // downscaled thumbnails of `images` (same order/faces), built by the cardThumbs integration
  artId?: string;              // MTGO art id (CardDataSource <ARTID>); shared by reprints reusing the art; maps ArtInFrame jpgs to cards
  artwork?: FileMeta;          // cropped card art (no frame), ingested from the client's ArtInFrame images (matched by artId)
  // Full-card images and cropped artwork ingested by cardImageIngestJob
  // (server/integrations/cardImageIngest.ts), matched by numeric filename (= _id).
  mtgoImage?: FileMeta;        // full card rendered by the MTGO client (cc/)
  scryfallImage?: FileMeta;    // full card downloaded from Scryfall (sc/)
  mtgoArtwork?: FileMeta;      // cropped art rendered by the MTGO client (ca/)
  scryfallArtwork?: FileMeta;  // cropped art from Scryfall (sa/)
  imageThumb?: FileMeta;       // webp thumb of scryfallImage ?? mtgoImage (cardThumbs integration, tc/)
  artworkThumb?: FileMeta;     // webp thumb of scryfallArtwork ?? mtgoArtwork (cardThumbs integration, ta/)
  // Card metadata pulled from the MTGO CardDataSource XML by importCardXml.
  manaCost?: string;           // normalized, e.g. "{2}{U}"
  manaCostRaw?: string;        // raw MTGO notation, e.g. "#2u-#2u-"
  cmc?: number;                // converted mana cost / mana value
  typeLine?: string;           // composed, e.g. "Legendary Creature — Human Wizard"
  colors?: string[];           // color letters, e.g. ["U"]
  colorIdentity?: string[];    // color identity letters (colors + costs in rules text)
  collectorNumber?: string;    // collector number within the set (COLLECTOR_INFO_STRING)
  power?: string;              // string to allow "*", "X", etc.
  toughness?: string;          // string, same reason as power
  oracleText?: string;         // rules text (REAL_ORACLETEXT_STRING)
  // Variant identity (XML). A name+set can have many printings — Regular,
  // Showcase, Borderless, Extended, Ghostfire… — and prices differ per
  // printing, so anything that writes prices must be able to tell them apart.
  cloneId?: number | null;     // foil only: the NONFOIL printing this mirrors (XML CLONE_ID). GoatBots keys its rows by the nonfoil id, so this is how a foil price finds its card.
  variant?: string;            // print-run label (COMPONENT_NAME_STRING), e.g. 'Cards Main Set', 'Cards Showcase Ugin Ten', 'Cards Borderless Dragons'
  frameStyle?: number | null;  // MTGO frame style code (XML FRAMESTYLE); differs per treatment within a set
  isPromoPrinting?: boolean;   // XML IS_PROMO: a special-treatment printing inside a standard set (unlike `promo`, which just means setName === 'PRM')
}

type Model = Card;
type Refs = References<Model>;

export const Cards = new Mongo.Collection<Card>('cards');

export const listCards = DeclareMethod<ListMethodArg<Card>, ListMethodResponse<Card, Refs>>('listCards');
export const getCard = DeclareMethod<string, GetMethodResponse<Card, Refs>>('getCard');
export const createCard = DeclareMethod<Partial<Card>, string>('createCard');
export const updateCard = DeclareMethod<Partial<Card>, string>('updateCard');
export const deleteCard = DeclareMethod<string, undefined>('deleteCard');

export interface CardXmlImportResult {
  rarities: number;
  sets: number;
  cardNames: number;
  cards: number;
  artwork: number;
  skipped: string[];
}
export const importCardXml = DeclareMethod<
  { files: Array<{ name: string; content: string; encoding?: 'base64' }> },
  CardXmlImportResult
>('importCardXml');

export const modelClass: ModelClass<Model, Refs> = {
  collection: Cards,
  idType: 'number',
  editableFields: [
    'name', 'setName', 'rarity', 'isfoil', 'legacy', 'oldset', 'promo',
    'booster', 'legendary', 'redeemable', 'pauper', 'sticky',
    'gbBuyPrice', 'gbSellPrice', 'manBuyPrice', 'manSellPrice', 'cashBuyPrice',
    'buyPriceMax', 'sellPriceMin', 'gbBuyMargin', 'gbSellMargin', 'fixedPrice',
    'images', 'thumbs', 'artId', 'artwork',
    'mtgoImage', 'scryfallImage', 'mtgoArtwork', 'scryfallArtwork',
    'imageThumb', 'artworkThumb',
  ],
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return { name: new RegExp(search, 'gi') };
  },
  listMethod: listCards,
  getMethod: getCard,
  createMethod: createCard,
  updateMethod: updateCard,
  deleteMethod: deleteCard,
  pathPrefix: '/cards',
  modelName: 'Card',
  pluralName: 'Cards',
};
